import React, { useState, useEffect } from 'react';
import '../Properties/property.scss';
import PropTypes from 'prop-types';
import DescriptionIcon from '@mui/icons-material/Description';
import CloseIcon from '@mui/icons-material/Close';
import PropertyLayout from './layout';
import { uploadFiles, addPropertyDetail, fetchRequiredDocuments, fetchFileById } from '../../Api/request/propertyDetail';
import UseAxios from '../../Api/http/http';
import { toast } from 'react-toastify';
import { propertyAPI } from '../../services/api';

const MAX_FILES_PER_BOX = 20;
const MAX_FILE_SIZE_MB = 10;
const ACCEPTED_FILE_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/jpg',
];

function UploadPropertyDocumentForm({ prevStep, onNext, onSave, onBackToDashboard, initialValues = {}, step = 9, totalSteps = 10, systemId, subType, isEdit = false, isView = false }) {
  // State for dynamic document fields
  const [documentFields, setDocumentFields] = useState([]);
  const [documentsCount, setDocumentsCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false); // Loading state for form submission
  const [submitError, setSubmitError] = useState(''); // Error state for submission
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [existingDocumentPresence, setExistingDocumentPresence] = useState({});

  // Main document fields
  // Additional document fields
  const ADDITIONAL_DOCUMENTS = [
    'Bank documents (if loans)',
    'Legal documents (if litigation)',
    'Electricity Bill',
    'Rent agreements',
    'Survey settlement records',
    'Possession certificate',
    'NOC from society builder',
    'Insurance documents',
  ];
  // Initialize form state dynamically
  const initializeFormState = (fields, existingValues = {}) => {
    const state = {};
    fields.forEach(field => {
      state[field] = existingValues[field] || [];
    });
    ADDITIONAL_DOCUMENTS.forEach(field => {
      state[field] = existingValues[field] || [];
    });
    return state;
  };

  const [form, setForm] = useState(() => {
    // Don't include property_documents from initialValues yet - they'll be loaded properly in useEffect
    // eslint-disable-next-line no-unused-vars
    const { property_documents, ...restInitialValues } = initialValues;
    return initializeFormState(documentFields, restInitialValues);
  });
  const [error, setError] = useState('');

  // Fetch required documents on component mount
  React.useEffect(() => {
    const fetchDocuments = async () => {
      try {
        setIsLoading(true);
        // Only call API if subType is available
        if (!subType) {
          console.warn('subType is not available, using default documents');
          setDocumentFields([]);
          setIsLoading(false);
          return;
        }
        const response = await UseAxios(fetchRequiredDocuments({ subType }));
        console.log('Fetched documents response:', response?.data);
        if (response?.data?.documents) {
          const fields = response.data.documents.map(doc => doc.document_name);
          console.log('Extracted document fields:', fields);
          setDocumentFields(fields);
          setDocumentsCount(response.data.documentsCount || fields.length);
          // Only update form if it's empty or if we're switching document types
          setForm(prevForm => {
            const hasExistingData = Object.keys(prevForm).some(key =>
              Array.isArray(prevForm[key]) && prevForm[key].length > 0,
            );

            if (!hasExistingData) {
              return { ...initializeFormState(fields, initialValues), ...initialValues };
            }

            // Merge new fields with existing form data
            const newState = initializeFormState(fields, prevForm);
            return { ...newState, ...prevForm };
          });
        } else {
          console.warn('No documents found in response or unexpected response format');
          setDocumentFields([]);
          setDocumentsCount(0);
        }
      } catch (error) {
        console.error('Error fetching required documents:', error);
        // Fallback to default fields if API fails
        const defaultFields = [
          'Sale Deed',
          'e-Khata',
          'Conversion Order/Copy',
          'EC',
          'Original Mother Deed',
          'Building Licence',
          'RERA Certificate',
          'Pollution Control Board',
          'Fire Dept NOC',
          'Sanction Plan Of Building',
        ];
        setDocumentFields(defaultFields);
        setDocumentsCount(defaultFields.length);
        // Only initialize if form is empty
        setForm(prevForm => {
          const hasExistingData = Object.keys(prevForm).some(key =>
            Array.isArray(prevForm[key]) && prevForm[key].length > 0,
          );

          if (!hasExistingData) {
            return { ...initializeFormState(defaultFields, initialValues), ...initialValues };
          }

          return prevForm;
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchDocuments();
  }, [subType]); // Add subType as dependency

  // Function to fetch existing document files for edit mode
  const fetchExistingDocuments = async (propertyDocuments) => {
    if (!propertyDocuments || typeof propertyDocuments !== 'object') return {};

    console.log('Fetching existing documents...', propertyDocuments);
    const fetchedDocuments = {};
    // Document key mapping from API to component keys
    const documentKeyMapping = {
      'tax_receipt': 'Tax Receipt',
      'sale_deed': 'Sale Deed',
      'khata': 'e-Khata',
      'fmb_nakshe': 'FMB / Nakshe',
      'conversion_order_copy': 'Conversion Order/Copy',
      'ec': 'EC',
      'rtc': 'RTC',
      'mutation': 'Mutation',
      'occupancy_certificate': 'Occupancy Certificate',
      'original_mother_deed': 'Original Mother Deed',
      'building_licence': 'Building Licence',
      'crz_noc': 'CRZ NOC',
      'rera_certificate': 'RERA Certificate',
      'pollution_control_board': 'Pollution Control Board',
      'fire_dept_noc': 'Fire Dept Noc',
      'airport_clearance': 'Airport Clearance',
      'sanction_plan_building': 'Sanction Plan Of Building',
      'bank_loan_documents': 'Bank documents (if loans)',
      'legal_documents': 'Legal documents (if litigation)',
      'electricity_bill': 'Electricity Bill',
      'rent_agreements': 'Rent agreements',
      'survey_settlement_records': 'Survey settlement records',
      'possession_certificate': 'Possession certificate',
      'noc_from_society_builder': 'NOC from society builder',
      'insurance_documents': 'Insurance documents',
    };

    for (const [apiKey, componentKey] of Object.entries(documentKeyMapping)) {
      if (propertyDocuments[apiKey] && Array.isArray(propertyDocuments[apiKey])) {
        console.log(`Fetching ${propertyDocuments[apiKey].length} files for ${componentKey}...`);
        const fetchedFiles = [];
        for (const fileInfo of propertyDocuments[apiKey]) {
          try {
            const response = await UseAxios(fetchFileById({ fileId: fileInfo.id }));
            if (response && response.data) {
              let blob;
              if (response.data instanceof Blob) {
                blob = response.data;
              } else if (response.data instanceof ArrayBuffer) {
                blob = new Blob([response.data], { type: fileInfo.type || 'application/octet-stream' });
              }
              if (blob) {
                const url = URL.createObjectURL(blob);
                const file = new File([blob], fileInfo.name, { type: fileInfo.type });
                fetchedFiles.push({
                  url,
                  name: fileInfo.name,
                  file,
                  type: fileInfo.type,
                  id: fileInfo.id,
                });
              }
            }
          } catch (error) {
            console.error(`Error fetching document file for ${componentKey}:`, error);
          }
        }
        fetchedDocuments[componentKey] = fetchedFiles;
        console.log(`Successfully fetched ${fetchedFiles.length} files for ${componentKey}`);
      }
    }
    return fetchedDocuments;
  };

  // Load existing document files when in edit mode
  useEffect(() => {
    const loadExistingFiles = async () => {
      if ((isEdit || isView) && initialValues && documentFields.length > 0) {
        setIsLoadingFiles(true);
        try {
          let existingDocuments = {};

          // Fetch existing documents
          if (initialValues.property_documents) {
            existingDocuments = await fetchExistingDocuments(initialValues.property_documents);
          }

          const presenceMap = {};
          Object.keys(existingDocuments).forEach((documentType) => {
            presenceMap[documentType] = Array.isArray(existingDocuments[documentType]) && existingDocuments[documentType].length > 0;
          });
          setExistingDocumentPresence(presenceMap);

          setForm(prev => {
            // Check if we already have user-uploaded documents that shouldn't be overridden
            const hasUserUploads = Object.keys(prev).some(key =>
              Array.isArray(prev[key]) && prev[key].length > 0,
            );

            if (hasUserUploads) {
              // Merge existing documents with user uploads
              const merged = { ...prev };
              Object.keys(existingDocuments).forEach(key => {
                if (merged[key] && Array.isArray(merged[key])) {
                  merged[key] = [...(existingDocuments[key] || []), ...merged[key]];
                } else {
                  merged[key] = existingDocuments[key] || [];
                }
              });
              return merged;
            }

            // Set the properly fetched documents
            return {
              ...prev,
              ...existingDocuments,
            };
          });
        } catch (error) {
          console.error('Error loading existing documents:', error);
          setError('Failed to load existing documents');
        } finally {
          setIsLoadingFiles(false);
        }
      }
    };

    loadExistingFiles();
  }, [initialValues, isEdit, isView, documentFields]);

  // Cleanup blob URLs when component unmounts or form data changes
  useEffect(() => {
    return () => {
      // Cleanup all blob URLs
      Object.values(form).forEach(fileArray => {
        if (Array.isArray(fileArray)) {
          fileArray.forEach(file => {
            if (file.url && file.url.startsWith('blob:')) {
              URL.revokeObjectURL(file.url);
            }
          });
        }
      });
    };
  }, [form]);

  // Sync initialValues if they change (for navigation between steps)
  useEffect(() => {
    if (initialValues && documentFields.length > 0 && !isEdit && !isView) {
      setForm((prev) => {
        // Check if initialValues has file data that should be preserved
        const hasInitialFiles = Object.keys(initialValues).some(key =>
          Array.isArray(initialValues[key]) && initialValues[key].length > 0,
        );

        // Check if current form has user uploads
        const hasUserUploads = Object.keys(prev).some(key =>
          Array.isArray(prev[key]) && prev[key].length > 0,
        );

        // If we have initial files or user uploads, merge them properly
        if (hasInitialFiles || hasUserUploads) {
          const merged = { ...prev };

          // Only update fields that don't have user uploads
          Object.keys(initialValues).forEach(key => {
            const hasUserFilesForField = merged[key] &&
              Array.isArray(merged[key]) &&
              merged[key].length > 0;

            if (!hasUserFilesForField && Array.isArray(initialValues[key])) {
              merged[key] = initialValues[key];
            }
          });

          return { ...merged, ...initialValues };
        }

        return prev;
      });
    }
  }, [initialValues, documentFields, isEdit, isView]);

  const getExistKey = (field) => {
    // Convert field name to camelCase and append 'Exist'
    return (
      field
        .replace(/[^a-zA-Z0-9]/g, ' ') // Remove non-alphanumeric
        .replace(/ (.)/g, (index, file) => file.toUpperCase()) // Capitalize after space
        .replace(/ /g, '') // Remove spaces
        .replace(/^(.)/, (index, file) => file.toLowerCase()) // Lowercase first char
      + 'Exist'
    );
  };

  const handleFiles = (field, files) => {
    setError('');
    setSubmitError(''); // Clear submit error when user starts uploading new files
    const fileArray = Array.from(files);
    const newFiles = [];
    if (form[field].length + fileArray.length > MAX_FILES_PER_BOX) {
      setError(`Maximum ${MAX_FILES_PER_BOX} files allowed for ${field}.`);
      return;
    }
    for (let file of fileArray) {
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        setError(`${file.name} exceeds ${MAX_FILE_SIZE_MB}MB.`);
        continue;
      }
      if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
        setError(`File type not supported: ${file.name}`);
        continue;
      }
      newFiles.push({
        url: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
        name: file.name,
        type: file.type,
        file,
      });
    }
    const existKey = getExistKey(field);
    setForm(prev => ({
      ...prev,
      [field]: [...prev[field], ...newFiles].slice(0, MAX_FILES_PER_BOX),
      [existKey]: [...prev[field], ...newFiles].length > 0,
    }));
  };

  const removeFile = (field, idx) => {
    const file = form[field]?.[idx];
    const fileIdToDelete =
      file?.id ||
      file?.fileId ||
      file?.file_id ||
      file?.driveFileId ||
      null;

    if (fileIdToDelete) {
      // Best-effort delete call; keep existing UI behavior unchanged on failure.
      void propertyAPI.deletePropertyImage(String(fileIdToDelete)).catch((error) => {
        console.warn('Failed to delete document from drive', {
          fileId: String(fileIdToDelete),
          error,
        });
      });
    }

    setForm(prev => {
      const file = prev[field][idx];
      // Revoke blob URLs for all files
      if (file && file.url && file.url.startsWith('blob:')) {
        URL.revokeObjectURL(file.url);
      }
      const newArr = prev[field].filter((file, index) => index !== idx);
      const existKey = getExistKey(field);
      return {
        ...prev,
        [field]: newArr,
        [existKey]: newArr.length > 0,
      };
    });
  };

  function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Prevent double submission
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      for (const field of [...documentFields, ...ADDITIONAL_DOCUMENTS]) {
        const filesForType = form[field] || [];
        const hadExistingForType = !!existingDocumentPresence[field];
        const shouldSaveInEdit = isEdit && (hadExistingForType || filesForType.length > 0);
        const shouldSaveInCreate = !isEdit && filesForType.length > 0;

        if (!shouldSaveInEdit && !shouldSaveInCreate) {
          continue;
        }

        const existingMeta = filesForType
          .filter(fileObj => fileObj?.id)
          .map(fileObj => ({
            id: fileObj.id,
            fileId: fileObj.id,
            name: fileObj.name,
            filename: fileObj.name,
            type: fileObj.type,
            documentType: field,
          }));

        let uploadedMeta = [];
        const formData = new FormData();
        formData.append('systemId', systemId);
        formData.append('type', 'Files');
        formData.append('subType', field);

        // Append only brand-new files (existing files have id and should not be re-uploaded)
        for (const fileObj of filesForType) {
          if (fileObj.file && !fileObj.id) {
            formData.append('images', fileObj.file, fileObj.name);
          }
        }

        if (filesForType.some(fileObj => fileObj.file && !fileObj.id)) {
          try {
            const response = await UseAxios(uploadFiles(formData));

            if (response?.data?.status !== 200) {
              throw new Error(response?.data?.message || 'File upload failed');
            }

            uploadedMeta = (response?.data?.receiptData || []).map(meta => ({
              ...meta,
              documentType: field,
            }));
          } catch (fileError) {
            console.error(`Error uploading ${field}:`, fileError);
            const errorMessage = `Failed to upload ${field}: ${fileError.message || 'Unknown error'}`;
            setSubmitError(errorMessage);
            toast.error(errorMessage);
            setIsSubmitting(false);
            return;
          }
        }

        const metaData = [...existingMeta, ...uploadedMeta];

        // Always call save API for editable/selected document types so backend can clear deletions.
        try {
          const payload = {
            property_id: systemId,
            documentSave: true,
            documentType: field,
            documentMetaData: metaData,
          };

          const saveResponse = await UseAxios(addPropertyDetail(payload));

          if (saveResponse?.data?.status !== 200) {
            throw new Error(saveResponse?.data?.message || 'Failed to save property details');
          }

          await sleep(500);
        } catch (saveError) {
          console.error(`Error saving ${field}:`, saveError);
          const errorMessage = `Failed to save ${field}: ${saveError.message || 'Unknown error'}`;
          setSubmitError(errorMessage);
          toast.error(errorMessage);
          setIsSubmitting(false);
          return;
        }
      }

      // If we reach here, all uploads were successful
      onNext(form);
    } catch (error) {
      console.error('Error during form submission:', error);
      const errorMessage = error.message || 'An unexpected error occurred';
      setSubmitError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };
  // Helper to save form data before navigating
  const handlePrev = () => {
    onSave(form); // Save current form data including uploaded files
    prevStep(); // Go to previous step
  };

  const handleNext = () => {
    onSave(form); // Save current form data
    onNext(form); // Pass form data to next step
  };

  return (
    <PropertyLayout step={step} totalSteps={totalSteps} systemId={systemId} onBackToDashboard={onBackToDashboard}>
      <div className='card' style={{ position: 'relative' }}>
        {/* Loading overlay during submission */}
        {isSubmitting && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            borderRadius: '8px',
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: '40px',
                height: '40px',
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #007bff',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 10px',
              }}></div>
              <p>Uploading documents...</p>
            </div>
          </div>
        )}

        <h3 className='card-title'>
          <DescriptionIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
          Property Documents
          {isEdit && (
            <span style={{ fontSize: '0.8em', color: '#666', marginLeft: '10px' }}>
              (Edit Mode)
            </span>
          )}
          {isView && (
            <span style={{ fontSize: '0.8em', color: '#666', marginLeft: '10px' }}>
              (View Mode)
            </span>
          )}
        </h3>

        {/* Display loading for existing files */}
        {isLoadingFiles && (
          <div className="loading-message" style={{
            padding: '10px',
            marginBottom: '15px',
            backgroundColor: '#e3f2fd',
            color: '#1976d2',
            borderRadius: '4px',
            border: '1px solid #bbdefb',
            textAlign: 'center',
          }}>
            Loading existing documents...
          </div>
        )}

        <div className='field-note' style={{ marginBottom: 16 }}>
          Upload the following documents based on your property type and sale type
        </div>

        <form onSubmit={handleSubmit}>
          {isLoading ? (
            <div className='form-group'>
              <p>Loading required documents for {subType || 'property'}...</p>
            </div>
          ) : documentFields.length === 0 ? (
            <div className='form-group'>
              <p>No required documents found for {subType || 'this property type'}.</p>
            </div>
          ) : (
            <>
              {/* Show submission error if any */}
              {submitError && (
                <div className='form-group'>
                  <div className='field-note-error' style={{ marginBottom: 16 }}>
                    {submitError}
                  </div>
                </div>
              )}

              <div className='form-group'>
                <label style={{ fontWeight: 600 }}>Document Upload Progress</label>
                {documentsCount > 0 && (
                  <div className='upload-progress-info'>
                    <span style={{ fontSize: 14, color: '#888' }}>
                      {documentsCount} required documents for {subType}
                    </span>
                  </div>
                )}
              </div>

              <div className='documents-grid'>
                {documentFields.map((field, idx) => (
                  <div key={idx} className='document-box'>
                    <p className='doc-label'>{field}</p>
                    <input
                      type='file'
                      id={`doc-${idx}`}
                      accept={ACCEPTED_FILE_TYPES.join(',')}
                      multiple
                      style={{ display: 'none' }}
                      onChange={event => handleFiles(field, event.target.files)}
                    />
                    {!isView && (
                      <button
                        type='button'
                        className='btn small'
                        onClick={() => document.getElementById(`doc-${idx}`).click()}
                      >
                        Choose Files
                      </button>
                    )}
                    {form[field] && form[field].length > 0 && (
                      <div className='preview-container'>
                        {form[field].map((file, fIdx) => (
                          <div key={fIdx} className='preview-wrapper'>
                            <span className='preview-label'>{file.name}</span>
                            {!isView && (
                              <span className='cancel-icon' onClick={() => removeFile(field, fIdx)}>
                                <CloseIcon fontSize='small' />
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {error && error.includes(field) && (
                      <div className='field-note-error'>{error}</div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {error && <div className='field-note-error'>{error}</div>}

          <div className='tips-section'>
            <h4>Document Upload Guidelines:</h4>
            <ul>
              <li>Accepted formats: JPG, PNG, PDF, DOC, DOCX</li>
              <li>Maximum 3 files allowed per document</li>
              <li>File size should not exceed 10MB each</li>
              <li>Ensure documents are legible and complete</li>
            </ul>
          </div>
          {/* --- Additional Documents Section --- */}
          <div className='additional-docs'>
            <h4>Additional Documents (Optional)</h4>
            <div className='additional-grid'>
              {[0, 1].map(col => (
                <div key={col} className='additional-docs-col'>
                  {ADDITIONAL_DOCUMENTS.slice(col * 4, col * 4 + 4).map((field, idx) => (
                    <div key={field} className='additional-doc-box'>
                      <p className='doc-label'>{field}</p>
                      {!isView && (
                        <button
                          type='button'
                          className='btn small choose-files-btn'
                          onClick={() => document.getElementById(`additional-doc-${col * 4 + idx}`).click()}
                        >
                          Choose Files
                        </button>
                      )}
                      <input
                        type='file'
                        id={`additional-doc-${col * 4 + idx}`}
                        accept={ACCEPTED_FILE_TYPES.join(',')}
                        multiple
                        style={{ display: 'none' }}
                        onChange={event => handleFiles(field, event.target.files)}
                      />
                      {error && error.includes(field) && (
                        <div className='field-note-error'>{error}</div>
                      )}
                      {form[field] && form[field].length > 0 && (
                        <div className='preview-container'>
                          {form[field].map((file, fIdx) => (
                            <div key={fIdx} className='preview-wrapper'>
                              <span className='preview-label'>{file.name}</span>
                              {!isView && (
                                <span className='cancel-icon' onClick={() => removeFile(field, fIdx)}>
                                  <CloseIcon fontSize='small' />
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
          <div className='form-actions'>
            <button
              type='button'
              className='btn secondary'
              onClick={handlePrev}
              disabled={isSubmitting}
            >
              Previous
            </button>
            {isView ? (
              <button
                type='button'
                className='btn primary'
                onClick={handleNext}
              >
                Next
              </button>
            ) : (
              <button
                type='submit'
                className='btn primary'
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Uploading...' : 'Next'}
              </button>
            )}
          </div>
        </form>
      </div>
    </PropertyLayout>
  );
}

UploadPropertyDocumentForm.propTypes = {
  prevStep: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onBackToDashboard: PropTypes.func,
  initialValues: PropTypes.object,
  step: PropTypes.number,
  totalSteps: PropTypes.number,
  systemId: PropTypes.string,
  subType: PropTypes.string,
  isEdit: PropTypes.bool,
  isView: PropTypes.bool,
};

export default UploadPropertyDocumentForm;
